import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Users, FileText, AlertCircle } from 'lucide-react';

const PendingCasesPerInvestigator = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingCases();
  }, []);

  const fetchPendingCases = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cases/pending-per-investigator');
      setData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching pending cases:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pending Cases Per Investigator</h1>
          <p className="text-gray-600 mt-1">View all pending cases assigned to each investigator</p>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No pending cases found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.map((item) => (
            <div key={item.investigator.id} className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Users className="w-6 h-6 text-blue-600" />
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {item.investigator.name}
                      </h2>
                      <p className="text-sm text-gray-500">{item.investigator.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                      {item.pendingCases} Pending {item.pendingCases === 1 ? 'Case' : 'Cases'}
                    </span>
                  </div>
                </div>
              </div>

              {item.cases.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p>No pending cases assigned</p>
                </div>
              ) : (
                <div className="p-6">
                  <div className="space-y-3">
                    {item.cases.map((caseItem) => (
                      <Link
                        key={caseItem._id}
                        to={`/cases/${caseItem._id}`}
                        className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-sm font-medium text-gray-900">
                                {caseItem.caseNumber}
                              </span>
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                {caseItem.status}
                              </span>
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  caseItem.priority === 'Urgent'
                                    ? 'bg-red-100 text-red-800'
                                    : caseItem.priority === 'High'
                                    ? 'bg-orange-100 text-orange-800'
                                    : caseItem.priority === 'Medium'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-green-100 text-green-800'
                                }`}
                              >
                                {caseItem.priority}
                              </span>
                            </div>
                            <h3 className="text-base font-medium text-gray-900 mb-1">
                              {caseItem.title}
                            </h3>
                            <p className="text-sm text-gray-600 line-clamp-2">
                              {caseItem.description || 'No description'}
                            </p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                              <span>Type: {caseItem.caseType}</span>
                              {caseItem.dueDate && (
                                <span>
                                  Due: {new Date(caseItem.dueDate).toLocaleDateString()}
                                </span>
                              )}
                              <span>
                                Created: {new Date(caseItem.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          {caseItem.dueDate && new Date(caseItem.dueDate) < new Date() && (
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingCasesPerInvestigator;


